import { useState, useEffect, useRef } from "react";

// #region THUMBNAIL CACHE
/**
 * In-memory cache for resized thumbnail data URLs.
 * Persists across component re-renders within the same session.
 * Key: `${originalUrl}_${width}x${height}`
 * Value: base64 data URL of the resized image
 */
const thumbnailCache = new Map();

/**
 * Generates a cache key for a thumbnail based on URL and dimensions.
 * @param {string} url - The original image URL.
 * @param {number} width - Target width.
 * @param {number} height - Target height.
 * @returns {string} The cache key.
 */
const getCacheKey = (url, width, height) => `${url}_${width}x${height}`;
// #endregion

/**
 * Resizes an image using the Canvas API.
 * @param {string} imageSrc - The source URL of the image to resize.
 * @param {number} targetWidth - The target width of the resized image.
 * @param {number} targetHeight - The target height of the resized image.
 * @returns {Promise<string>} A promise that resolves to the base64 data URL of the resized image.
 */
const resizeImage = (imageSrc, targetWidth, targetHeight) => {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = "anonymous"; // Handle CORS for external images

        img.onload = () => {
            const canvas = document.createElement("canvas");
            canvas.width = targetWidth;
            canvas.height = targetHeight;

            const ctx = canvas.getContext("2d");

            // Calculate aspect ratio for "cover" behavior
            const imgAspect = img.width / img.height;
            const canvasAspect = targetWidth / targetHeight;

            let sourceX = 0,
                sourceY = 0,
                sourceWidth = img.width,
                sourceHeight = img.height;

            if (imgAspect > canvasAspect) {
                // Image is wider - crop horizontally
                sourceWidth = img.height * canvasAspect;
                sourceX = (img.width - sourceWidth) / 2;
            } else {
                // Image is taller - crop vertically
                sourceHeight = img.width / canvasAspect;
                sourceY = (img.height - sourceHeight) / 2;
            }

            // Draw the cropped and resized image
            ctx.drawImage(img, sourceX, sourceY, sourceWidth, sourceHeight, 0, 0, targetWidth, targetHeight);

            // Convert to base64 data URL (JPEG for smaller size)
            const dataUrl = canvas.toDataURL("image/jpeg", 0.8);
            resolve(dataUrl);
        };

        img.onerror = () => {
            reject(new Error("Failed to load image for resizing"));
        };

        img.src = imageSrc;
    });
};

/**
 * A lazy-loading thumbnail component that loads and resizes images on-demand
 * using Intersection Observer and Canvas API.
 *
 * Features:
 * - Lazy loading: Only loads when thumbnail scrolls into view
 * - Client-side resizing: Resizes full images to thumbnail size using Canvas
 * - In-memory caching: Caches resized thumbnails to avoid re-processing
 *
 * @component
 * @param {Object} props - Component props.
 * @param {string} props.src - The source URL of the full-size image.
 * @param {string} [props.alt=""] - Alt text for the image.
 * @param {number} [props.width=25] - Target width of the thumbnail.
 * @param {number} [props.height=39] - Target height of the thumbnail.
 * @param {string} [props.className=""] - CSS class name for the image.
 * @param {Object} [props.style={}] - Inline styles for the image.
 * @param {Function} [props.onClick] - Click handler for the thumbnail.
 * @param {string} [props.placeholderColor="#141922"] - Background color while loading.
 * @returns {JSX.Element} The rendered LazyThumbnail component.
 */
export default function LazyThumbnail({
    src,
    alt = "",
    width = 25,
    height = 39,
    className = "",
    style = {},
    onClick,
    placeholderColor = "#141922"
}) {
    // #region VARS
    const [thumbnailSrc, setThumbnailSrc] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [hasError, setHasError] = useState(false);
    const [isInView, setIsInView] = useState(false);
    const containerRef = useRef(null);
    // #endregion

    // #region EFFECTS
    // Set up Intersection Observer for lazy loading
    useEffect(() => {
        const observer = new IntersectionObserver(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        setIsInView(true);
                        observer.unobserve(entry.target);
                    }
                });
            },
            {
                rootMargin: "100px", // Start loading 100px before entering viewport
                threshold: 0
            }
        );

        if (containerRef.current) {
            observer.observe(containerRef.current);
        }

        return () => {
            if (containerRef.current) {
                observer.unobserve(containerRef.current);
            }
        };
    }, []);

    // Load and resize image when it comes into view
    useEffect(() => {
        if (!isInView || !src) return;

        const loadAndResizeThumbnail = async () => {
            const cacheKey = getCacheKey(src, width, height);

            // Check cache first
            if (thumbnailCache.has(cacheKey)) {
                setThumbnailSrc(thumbnailCache.get(cacheKey));
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                setHasError(false);

                const resizedDataUrl = await resizeImage(src, width, height);

                // Cache the result
                thumbnailCache.set(cacheKey, resizedDataUrl);

                setThumbnailSrc(resizedDataUrl);
                setIsLoading(false);
            } catch (error) {
                console.warn("LazyThumbnail: Failed to load/resize image:", src, error);
                setHasError(true);
                setIsLoading(false);
            }
        };

        loadAndResizeThumbnail();
    }, [isInView, src, width, height]);
    // #endregion

    // #region RENDER
    const containerStyle = {
        width: `${width}px`,
        height: `${height}px`,
        backgroundColor: placeholderColor,
        borderRadius: "4px",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: onClick ? "pointer" : "default",
        ...style
    };

    const imageStyle = {
        width: "100%",
        height: "100%",
        objectFit: "cover",
        opacity: isLoading ? 0 : 1,
        transition: "opacity 0.2s ease-in-out"
    };

    return (
        <div ref={containerRef} className={className} style={containerStyle} onClick={onClick}>
            {isLoading && !hasError && (
                <div
                    style={{
                        width: "100%",
                        height: "100%",
                        background: `linear-gradient(90deg, ${placeholderColor} 25%, #1a2030 50%, ${placeholderColor} 75%)`,
                        backgroundSize: "200% 100%",
                        animation: "shimmer 1.5s infinite"
                    }}
                />
            )}
            {hasError && (
                <div
                    style={{
                        width: "100%",
                        height: "100%",
                        backgroundColor: placeholderColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#666",
                        fontSize: "8px"
                    }}>
                    !
                </div>
            )}
            {thumbnailSrc && !hasError && <img src={thumbnailSrc} alt={alt} style={imageStyle} draggable={false} />}
        </div>
    );
    // #endregion
}

// Add shimmer animation CSS if not already present
if (typeof document !== "undefined") {
    const styleId = "lazy-thumbnail-styles";
    if (!document.getElementById(styleId)) {
        const style = document.createElement("style");
        style.id = styleId;
        style.textContent = `
            @keyframes shimmer {
                0% { background-position: -200% 0; }
                100% { background-position: 200% 0; }
            }
        `;
        document.head.appendChild(style);
    }
}
